import { OvertimeRequest, User } from '@/types'
import { OvertimeStatus, UserRole } from '@/types/enums'
import { toast } from "sonner"
import { checkEmployeeOvertimeEligibility } from './eligibilityUtils'

export const handleSubmitOvertimeRequest = async (
  user: User | null,
  overtimeRequests: OvertimeRequest[],
  setOvertimeRequests: React.Dispatch<React.SetStateAction<OvertimeRequest[]>>,
  request: Omit<OvertimeRequest, 'id' | 'employeeId' | 'employeeName' | 'status' | 'submittedAt'>
): Promise<boolean> => {
  try {
    if (!user) {
      toast.error("You must be logged in to submit requests")
      return false
    }

    const newRequest = {
      id: `${Date.now()}`,
      employeeId: user.id,
      employeeName: user.name,
      status: OvertimeStatus.PENDING,
      submittedAt: new Date().toISOString(),
      ...request
    } as unknown as OvertimeRequest
    
    setOvertimeRequests(prev => [newRequest, ...prev])
    
    toast.success("Request submitted", {
      description: "Your overtime request has been submitted successfully"
    })
    
    return true
  } catch {
    toast.error("Submission failed", {
      description: "There was an error submitting your request"
    })
    return false
  }
}

export const handleUpdateRequestStatus = async (
  user: User | null, 
  setOvertimeRequests: React.Dispatch<React.SetStateAction<OvertimeRequest[]>>,
  requestId: string, 
  status: OvertimeStatus, 
  comment: string
): Promise<boolean> => {
  try {
    if (!user) {
      toast.error("You must be logged in to update requests")
      return false
    }

    setOvertimeRequests(prev => 
      prev.map(req => 
        req.id === requestId
          ? {
              ...req,
              status,
              updatedAt: new Date().toISOString(),
              approverId: user.id,
              approverName: user.name,
              approverComment: comment
            }
          : req
      )
    )
    
    const action = status === OvertimeStatus.APPROVED ? OvertimeStatus.APPROVED : OvertimeStatus.REJECTED
    
    toast.success(`Request ${action}`, {
      description: `The overtime request has been ${action} successfully`
    })
    
    return true
  } catch {
    toast.error("Update failed", {
      description: "There was an error updating the request"
    })
    return false
  }
}

export const submitDepartmentOvertimeRequest = async (
  user: User | null,
  employees: User[],
  overtimeRequests: OvertimeRequest[],
  setOvertimeRequests: React.Dispatch<React.SetStateAction<OvertimeRequest[]>>,
  requests: Array<Omit<OvertimeRequest, 'id' | 'status' | 'submittedAt' | 'submittedById' | 'submittedByName'>>
): Promise<boolean> => {
  try {
    if (!user) {
      toast.error("You must be logged in to submit department requests")
      return false
    }

    const newRequestPromises = requests.map(async request => {
      try {
        const employee = employees.find(emp => emp.id === request.employeeId)
        
        const employeeIdStr = String((request as unknown as { employeeId?: string; employee_id?: string }).employeeId ?? (request as unknown as { employee_id?: string }).employee_id ?? '')
        const hoursNum = Number((request as unknown as { hours?: number | string }).hours ?? 0)
        const eligibility = await checkEmployeeOvertimeEligibility(
          employee,
          employeeIdStr,
          hoursNum,
          overtimeRequests
        )
        
        // Check if eligible
        if (!eligibility.eligible) {
          toast.warning(`Employee ${request.employeeName} not eligible for overtime: ${eligibility.reason}`)
          return null
        }
        
        const status = eligibility.requiresVPApproval ? OvertimeStatus.PENDING_VP_APPROVAL : OvertimeStatus.PENDING
        
        // Get worker type safely
        const workerTypeValue = (employee?.workerType || 'local')
        
        return ({
          id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          status,
          submittedAt: new Date().toISOString(),
          submittedById: user.id,
          submittedByName: user.name,
          employeeName: employee?.name || 'Unknown Employee',
          vpApprovalRequired: eligibility.requiresVPApproval || false,
          workerType: workerTypeValue,
          ...request
        } as unknown) as OvertimeRequest
      } catch {
        // Re-throw the error
      }
    })
    
    const newRequests = (await Promise.all(newRequestPromises)).filter(Boolean) as OvertimeRequest[]
    
    if (newRequests.length === 0) {
      return false
    }
    
    setOvertimeRequests(prev => [...newRequests, ...prev])
    
    toast.success("Requests submitted", {
      description: `${newRequests.length} overtime requests have been submitted`
    })
    
    return true
  } catch {
    toast.error("Submission failed", {
      description: "There was an error submitting the requests"
    })
    return false
  }
}

export const approveOvertimeRequestAsVP = async (
  user: User | null,
  setOvertimeRequests: React.Dispatch<React.SetStateAction<OvertimeRequest[]>>,
  requestId: string, 
  comment: string
): Promise<boolean> => {
  try {
    if (!user) {
      toast.error("You must be logged in to approve requests")
      return false
    }
    
    if (user.role !== UserRole.SUPER_ADMIN) {
      toast.error("Unauthorized", {
        description: "Only Super Admin users can approve these requests"
      })
      return false
    }

    setOvertimeRequests(prev => 
      prev.map(req => 
        req.id === requestId
          ? {
              ...req,
              status: OvertimeStatus.APPROVED,
              updatedAt: new Date().toISOString(),
              vpApproverId: user.id,
              vpApproverName: user.name,
              vpApproverComment: comment
            }
          : req
      )
    )
    
    toast.success("Request approved", {
      description: "The overtime request exceeding 104 hours has been approved"
    })
    
    return true
  } catch {
    toast.error("Approval failed", {
      description: "There was an error approving the request"
    })
    return false
  }
}
